/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { CellContext } from '@tanstack/react-table';
import { getMergedIpr } from '@upright/common/model/input/companies/products/product-input-ui/utils';
import styled from 'styled-components';
import {
  PiuProductState,
  ProductMatchStatus,
} from '@upright/common/model/input/companies/products/product-input-ui/types';
import { ProductSelector } from './product-selector';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { ResolvedIpr } from '@upright/common/model/input/companies/products/propagate-ipr-attributes';
import { buildAttributeGroups } from '@upright/common/model/input/companies/products/ipr/attribute-groups';
import { AttributeGroup } from '../ipr-attribute-groups';

/** Cell component showing the matched product name for the "Matching" column (team users only). */
export function ProductMatchingCell(cellProps: CellContext<PiuProductState, string>) {
  const product = cellProps.row.original;
  if (product.productMatchStatus === ProductMatchStatus.RUNNING) {
    return (
      <SkeletonWrap>
        <Skeleton count={1} width="100%" height="1.4em" />
      </SkeletonWrap>
    );
  }

  return (
    <ModelledAsContainer>
      <CoreProductRow>
        <ProductSelector product={product} />
      </CoreProductRow>
    </ModelledAsContainer>
  );
}

/** Cell component showing IPR attributes for the "Attributes" column (Enterprise + team users). */
export function ProductAttributesCell(cellProps: CellContext<PiuProductState, string>) {
  const product = cellProps.row.original;
  if (product.productMatchStatus === ProductMatchStatus.RUNNING) {
    return (
      <SkeletonWrap>
        <Skeleton count={1} width="100%" height="1.1em" />
        <Skeleton count={1} width="100%" height="1.1em" />
        <Skeleton count={1} width="100%" height="1.1em" />
      </SkeletonWrap>
    );
  }

  // PLT-5695: render the RESOLVED attributes (own merged with whatever cascades from ancestors),
  // distinguishing values that arrive only via inheritance. `resolvedIpr` is annotated onto each row
  // by the hierarchy table (`resolveIprAttributesRecursive`); the flat List view leaves it absent,
  // so we fall back to the row's own merged IPR with nothing inherited — byte-identical to the
  // pre-PLT-5695 display.
  const rowWithInheritance = product as PiuProductState & Partial<ResolvedIpr>;
  const ownIpr = getMergedIpr({ ipr: product.ipr, manualIprEdits: product.manualIprEdits });
  const resolvedIpr = rowWithInheritance.resolvedIpr ?? ownIpr;

  const groups = buildAttributeGroups(resolvedIpr, ownIpr);

  return (
    <ModelledAsContainer>
      {groups.map(group => (
        <AttributeGroup key={group.title} title={group.title} chips={group.chips} />
      ))}
    </ModelledAsContainer>
  );
}

const ModelledAsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CoreProductRow = styled.div`
  display: flex;
  gap: var(--space__xs);
  font-size: var(--font-size__md);
  margin-bottom: var(--space__xs);
`;

const SkeletonWrap = styled.div`
  width: 100%;
  gap: var(--space__xs);
  display: flex;
  flex-direction: column;
`;
